import { createFileRoute } from "@tanstack/react-router";
import { pageSchemaScript, PERSON_ID, PORTRAIT_ID } from "@/seo/schema";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { SITE, SOCIAL, WHATSAPP_HREF } from "@/lib/site";
import portraitHero from "@/assets/portrait-hero.webp";
import {
  SECTION_OG,
  sectionImageMeta,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  SITE_ORIGIN,
} from "@/lib/ogSections";


import {
  ArrowLeft,
  ArrowRight,
  Check,
  Mail,
  Linkedin,
  Calendar,
  Send,
  Phone,
  MapPin,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { LinkedInCarousel } from "@/components/LinkedInCarousel";
import { SocialIcons } from "@/components/SocialIcons";
import { QuickContactForm } from "@/components/QuickContactForm";
import {
  ctaClick,
  trackCta,
  getVisitorSessionId,
  getLastPlacement,
  type CtaChannel,
} from "@/lib/ctaTracking";
import { currentVariant } from "@/lib/abTest";
import { useServerFn } from "@tanstack/react-start";
import { submitLead } from "@/lib/leads.functions";
import { detectSpam } from "@/lib/leadValidation";
import { hreflangLinks } from "@/lib/hreflang";

const CALENDAR_LINK = "https://calendly.com/arifkhan-altimaglobal/consultation";

/** Visible FAQ on this page — also published as FAQPage JSON-LD below. */
const CONTACT_FAQS: { q: string; a: string }[] = [
  {
    q: "How do I start a conversation about a project?",
    a: "Use the Let's Talk form on this page with a short brief — site type, location, stage and the outcome you want. You can also reach Arif Khan directly on WhatsApp, phone or email; every channel reaches the same team.",
  },
  {
    q: "What information helps at the first conversation?",
    a: "Project type and location, current stage (concept, tender, construction or retrofit), the systems in scope — security, automation, home cinema or auditorium AV — and any deadline you are working to. Drawings are useful but not required to start.",
  },
  {
    q: "Which locations are covered?",
    a: "Projects across India, with the closest coverage in Delhi NCR — New Delhi, Noida, Gurugram, Faridabad and Ghaziabad — plus work in Mumbai, Bengaluru and other North India locations.",
  },
  {
    q: "What happens after I send a brief?",
    a: "You get a reply on the same channel you used. The first conversation is about objectives, constraints and phasing — technologies and vendors are selected afterwards, once the brief is clear.",
  },
  {
    q: "Which types of work is this advisory for?",
    a: "Security infrastructure and electronic security, perimeter protection, smart automation, luxury home cinema, and auditorium and corporate AV — planned as one integrated program rather than separate packages.",
  },
];

const CONTACT_TITLE = "Let's Talk — Arif Khan | Building Technology Advisory";
const CONTACT_DESC =
  "Let's talk about security infrastructure, smart automation, luxury cinema and AV environments. Share your project brief with Arif Khan.";

export const Route = createFileRoute("/contact")({
  component: ConsultationPage,
  head: () => ({
    meta: [
      { title: CONTACT_TITLE },
      { name: "description", content: CONTACT_DESC },
      { property: "og:title", content: CONTACT_TITLE },
      {
        property: "og:description",
        content:
          "Let's talk — discuss objectives before technologies are selected, with a phased plan for your environment.",
      },
      { property: "og:url", content: "https://arifkhanglobal.com/contact" },
      { property: "og:type", content: "website" },
      ...sectionImageMeta(
        SECTION_OG.contact,
        "Let's Talk — Arif Khan, Building Technology Advisory",
      ),

    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/contact" },
      ...hreflangLinks("/contact"),
      { rel: "alternate", type: "text/markdown", href: "https://arifkhanglobal.com/contact.md", title: "Markdown mirror" },
    ],
    scripts: [
      pageSchemaScript(
        {
          path: "/contact",
          type: "ContactPage",
          name: CONTACT_TITLE,
          description: CONTACT_DESC,
          breadcrumb: [{ name: "Let's Talk", url: "/contact" }],
          extra: {
            mainEntity: { "@id": PERSON_ID },
            primaryImageOfPage: { "@id": PORTRAIT_ID },
            potentialAction: {
              "@type": "CommunicateAction",
              "@id": "https://arifkhanglobal.com/contact#lets-talk",
              name: "Let's Talk",
              description: "Share a project brief and start a conversation with Arif Khan.",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://arifkhanglobal.com/contact",
                name: "Let's Talk",
              },
            },
          },
        },
        [
          {
            "@type": "FAQPage",
            "@id": "https://arifkhanglobal.com/contact#faq",
            url: "https://arifkhanglobal.com/contact",
            name: "Let's Talk — frequently asked questions",
            inLanguage: "en-IN",
            isPartOf: { "@id": "https://arifkhanglobal.com/contact#webpage" },
            mainEntity: CONTACT_FAQS.map((f, i) => ({
              "@type": "Question",
              "@id": `https://arifkhanglobal.com/contact#faq-${i + 1}`,
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      ),
    ],

  }),
});

type FormState = {
  discuss: string[];
  projectType: string;
  location: string;
  budget: string;
  timeline: string;
  name: string;
  email: string;
  company: string;
  message: string;
};

const INITIAL: FormState = {
  discuss: [],
  projectType: "",
  location: "",
  budget: "",
  timeline: "",
  name: "",
  email: "",
  company: "",
  message: "",
};

const DISCUSS_OPTIONS = [
  "Security Infrastructure",
  "Perimeter Protection",
  "CCTV & Command Centre",
  "Access Control & Biometrics",
  "Fire Alarm & Life Safety",
  "Entrance, Gate & Parking Automation",
  "Smart Automation",
  "Luxury Home Cinema",
  "Auditorium AV",
  "Enterprise AV / Boardroom",
  "Intelligent Building / IBMS",
  "Technology Strategy / Project Review",
  "Other",
];

const PROJECT_TYPES = [
  "Luxury Residence",
  "Villa",
  "Apartment",
  "Industrial Facility",
  "Warehouse",
  "Corporate Office",
  "Auditorium",
  "Hospitality",
  "Government",
  "Infrastructure",
  "Other",
];

const BUDGETS = [
  "Below ₹5 Lakhs",
  "₹5–20 Lakhs",
  "₹20–50 Lakhs",
  "₹50 Lakhs–1 Crore",
  "₹1–5 Crore",
  "Above ₹5 Crore",
  "Prefer not to say",
];

const TIMELINES = [
  "Immediate",
  "1–3 Months",
  "3–6 Months",
  "6–12 Months",
  "Planning stage",
];

const STEPS = [
  "Focus",
  "Project",
  "Location",
  "Budget",
  "Timeline",
  "Details",
] as const;

function ConsultationPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [mode, setMode] = useState<"guided" | "quick">("guided");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [hp, setHp] = useState("");
  const mountedAt = useRef(0);
  const send = useServerFn(submitLead);

  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  const total = STEPS.length;
  const progress = useMemo(() => ((step + 1) / total) * 100, [step, total]);

  const canAdvance = useMemo(() => {
    switch (step) {
      case 0:
        return form.discuss.length > 0;
      case 1:
        return !!form.projectType;
      case 2:
        return form.location.trim().length > 1;
      case 3:
        return !!form.budget;
      case 4:
        return !!form.timeline;
      case 5:
        return (
          form.name.trim().length > 1 &&
          /.+@.+\..+/.test(form.email)
        );
      default:
        return false;
    }
  }, [step, form]);

  const next = () => {
    if (step < total - 1) {
      setStep((s) => s + 1);
      return;
    }
    if (canAdvance) void submitGuided();
  };

  const submitGuided = async () => {
    setSendError(null);
    // Spam protection: hidden honeypot + minimum time-on-form + content checks.
    if (hp.trim().length > 0) {
      setSubmitted(true);
      return;
    }
    const brief = [
      `Focus: ${form.discuss.join(", ") || "—"}`,
      `Project type: ${form.projectType || "—"}`,
      `Location: ${form.location || "—"}`,
      `Budget: ${form.budget || "—"}`,
      `Timeline: ${form.timeline || "—"}`,
      "",
      form.message || "(no additional notes)",
    ].join("\n");

    const spam = detectSpam({ name: form.name, message: brief, company: form.company });
    if (spam) {
      setSendError(spam);
      return;
    }

    setSending(true);
    try {
      await send({
        data: {
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          message: brief,
          source: "contact_guided_form",
          hp,
          elapsedMs: mountedAt.current ? Date.now() - mountedAt.current : 0,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          sessionId: getVisitorSessionId() || undefined,
          variant: currentVariant() || undefined,
          path: typeof window !== "undefined" ? window.location.pathname : undefined,
          placement: getLastPlacement() || "contact_guided_form",
        },
      });
      trackCta("form", "contact_guided_form");
      setSubmitted(true);
    } catch (err) {
      setSendError(
        err instanceof Error ? err.message : "Could not send your brief. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const started = useRef(false);
  const markStarted = () => {
    if (started.current) return;
    started.current = true;
    trackCta("form_start", "contact_guided_form");
  };

  const toggleDiscuss = (v: string) => {
    markStarted();
    setForm((f) => ({
      ...f,
      discuss: f.discuss.includes(v) ? f.discuss.filter((x) => x !== v) : [...f.discuss, v],
    }));
  };


  return (
    <PageShell>
      {/* HERO */}
      <section className="relative pt-40 pb-16 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div
          className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
        />
        <div className="container-page relative">
          <div className="max-w-4xl">
            <span className="eyebrow">Project Review</span>
            <h1 className="mt-5 text-balance text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95]">
              Let's design intelligent environments.
            </h1>
            <p className="mt-6 max-w-2xl text-pretty text-lg text-ink-soft leading-relaxed">
              Discuss security infrastructure, smart automation, luxury home cinema and technology
              environments tailored to your requirements.
            </p>
            <p className="mt-4 max-w-2xl text-sm text-ink-soft/80">
              Every project begins with understanding objectives before selecting technologies.
            </p>
          </div>
        </div>
      </section>

      {/* CONSULTATION FLOW */}
      <section className="py-20 md:py-28">
        <div className="container-page grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT — portrait + quote */}
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative">
              <div className="relative overflow-hidden rounded-3xl border border-border bg-surface aspect-[4/5]">
                <img
                  src={portraitHero}
                  alt="Portrait of Arif Khan, Security Infrastructure & Building Technology Strategist at Altima Global, New Delhi"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div
                  className="absolute inset-x-0 bottom-0 p-6 md:p-8"
                  style={{ background: "linear-gradient(to top, hsl(var(--background) / 0.85), transparent)" }}
                >
                  <div className="text-xs font-semibold uppercase tracking-widest text-ink-soft mb-2">
                    {SITE.role}
                  </div>
                  <div className="text-lg font-medium">{SITE.name}</div>
                </div>
              </div>
              <figure className="mt-8 rounded-2xl border border-border/60 bg-surface/60 backdrop-blur-md p-6 md:p-8">
                <div className="text-4xl leading-none text-primary/60 font-serif">&ldquo;</div>
                <blockquote className="mt-2 text-lg md:text-xl font-medium leading-snug text-balance">
                  The right technology decisions happen before products are selected.
                </blockquote>
                <figcaption className="mt-4 text-xs font-semibold uppercase tracking-widest text-ink-soft">
                  — Arif Khan
                </figcaption>
              </figure>
            </div>
          </Reveal>

          {/* RIGHT — multi-step */}
          <Reveal delay={0.05} className="lg:col-span-7">
            <div
              className="relative rounded-3xl border border-border/70 bg-surface/40 backdrop-blur-xl p-6 md:p-10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.35)]"
              style={{ backgroundImage: "linear-gradient(180deg, hsl(var(--surface) / 0.6), hsl(var(--background) / 0.4))" }}
            >
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={() => setMode((m) => (m === "guided" ? "quick" : "guided"))}
                  className="text-xs font-semibold uppercase tracking-widest text-ink-soft hover:text-primary transition"
                >
                  {mode === "guided" ? "Prefer a quick message? →" : "← Back to guided form"}
                </button>
              </div>
              {mode === "quick" && !submitted && <QuickContactForm />}
              {mode === "guided" && !submitted && (
                <>

                  {/* progress */}
                  <div className="flex items-center justify-between mb-2 text-xs font-semibold uppercase tracking-widest text-ink-soft">
                    <span>
                      Step {step + 1} of {total}
                    </span>
                    <span>{STEPS[step]}</span>
                  </div>
                  <div className="h-[2px] w-full bg-border/60 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-primary"
                      initial={false}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>

                  <div className="mt-10 min-h-[360px]">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {step === 0 && (
                          <StepBlock
                            title="What would you like to discuss?"
                            hint="Select all that apply."
                          >
                            <div className="grid sm:grid-cols-2 gap-3">
                              {DISCUSS_OPTIONS.map((opt) => (
                                <ChoiceChip
                                  key={opt}
                                  label={opt}
                                  selected={form.discuss.includes(opt)}
                                  onClick={() => toggleDiscuss(opt)}
                                  multi
                                />
                              ))}
                            </div>
                          </StepBlock>
                        )}

                        {step === 1 && (
                          <StepBlock title="What kind of project?">
                            <div className="grid sm:grid-cols-2 gap-3">
                              {PROJECT_TYPES.map((opt) => (
                                <ChoiceChip
                                  key={opt}
                                  label={opt}
                                  selected={form.projectType === opt}
                                  onClick={() => setForm((f) => ({ ...f, projectType: opt }))}
                                />
                              ))}
                            </div>
                          </StepBlock>
                        )}

                        {step === 2 && (
                          <StepBlock
                            title="Where is the project located?"
                            hint="City or region. Pan-India and select international engagements considered."
                          >
                            <input
                              autoFocus
                              aria-label="Project location (city or region)"
                              value={form.location}
                              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                              placeholder="e.g. Gurugram, Mumbai, Dubai"
                              className="w-full rounded-2xl border border-border bg-background/60 px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                            />
                          </StepBlock>
                        )}

                        {step === 3 && (
                          <StepBlock title="Estimated project budget">
                            <div className="grid sm:grid-cols-2 gap-3">
                              {BUDGETS.map((opt) => (
                                <ChoiceChip
                                  key={opt}
                                  label={opt}
                                  selected={form.budget === opt}
                                  onClick={() => setForm((f) => ({ ...f, budget: opt }))}
                                />
                              ))}
                            </div>
                          </StepBlock>
                        )}

                        {step === 4 && (
                          <StepBlock title="Expected timeline">
                            <div className="grid sm:grid-cols-2 gap-3">
                              {TIMELINES.map((opt) => (
                                <ChoiceChip
                                  key={opt}
                                  label={opt}
                                  selected={form.timeline === opt}
                                  onClick={() => setForm((f) => ({ ...f, timeline: opt }))}
                                />
                              ))}
                            </div>
                          </StepBlock>
                        )}

                        {step === 5 && (
                          <StepBlock
                            title="How can Arif's team reach you?"
                            hint="Shared only with Arif. No marketing, no third parties."
                          >
                            <div className="grid sm:grid-cols-2 gap-4">
                              <TextField label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} required />
                              <TextField label="Email" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} required />
                              <TextField label="Company (optional)" value={form.company} onChange={(v) => setForm((f) => ({ ...f, company: v }))} />
                            </div>
                            <div className="mt-4">
                              <label htmlFor="contact-step-message" className="text-xs font-semibold uppercase tracking-widest text-ink-soft mb-2 block">
                                Anything else?
                              </label>
                              <textarea
                                id="contact-step-message"
                                rows={4}
                                value={form.message}
                                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                                placeholder="Site context, stakeholders, constraints, or what success looks like."
                                className="w-full rounded-2xl border border-border bg-background/60 px-5 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                              />
                            </div>
                          </StepBlock>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {sendError && (
                    <p className="mt-6 text-sm text-red-500" role="alert">
                      {sendError}
                    </p>
                  )}

                  {/* Honeypot — hidden from humans, filled by bots. */}
                  <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
                    <label htmlFor="ak-website">Company website</label>
                    <input
                      id="ak-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={hp}
                      onChange={(e) => setHp(e.target.value)}
                    />
                  </div>

                  {/* nav */}
                  <div className="mt-8 flex items-center justify-between gap-4">
                    <button
                      type="button"
                      onClick={back}
                      disabled={step === 0}
                      className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-ink-soft hover:text-foreground disabled:opacity-30 disabled:pointer-events-none transition"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button
                      type="button"
                      onClick={next}
                      disabled={!canAdvance || sending}
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-30 disabled:pointer-events-none transition"
                    >
                      {step === total - 1 ? (
                        sending ? (
                          <>Sending…</>
                        ) : (
                          <>Submit <Send className="h-4 w-4" /></>
                        )
                      ) : (
                        <>Continue <ArrowRight className="h-4 w-4" /></>
                      )}
                    </button>
                  </div>
                </>
              )}

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-10 md:py-16"
                >
                  <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-3xl md:text-4xl font-semibold tracking-tight text-balance">
                    Thank you. Your project brief has been received.
                  </h3>
                  <p className="mt-4 max-w-xl mx-auto text-ink-soft leading-relaxed">
                    We will review the requirement and respond by email with the next practical step.
                  </p>
                  <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      onClick={ctaClick("email", "contact_thankyou")}
                      href={`mailto:${SITE.email}?subject=${encodeURIComponent("Let's Talk — Project Requirement")}`}
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
                    >
                      <Mail className="h-4 w-4" /> Let's Talk by Email
                    </a>
                    <a
                      href={CALENDAR_LINK}
                      onClick={ctaClick("calendar", "contact_thankyou")}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/60 px-7 py-3.5 text-sm font-medium hover:border-primary transition"
                    >
                      <Calendar className="h-4 w-4" /> Let's Talk
                    </a>
                  </div>
                </motion.div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIRECT CONTACT SHORTCUTS */}
      <section className="pb-28">
        <div className="container-page">
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">Direct Channels</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              Prefer to reach Arif directly?
            </h2>
          </div>
          <address className="not-italic mb-4 rounded-2xl border border-border bg-surface/60 p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-primary">
                <MapPin className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Office Address
                </div>
                <p className="mt-1.5 text-sm leading-relaxed">
                  4th Floor, Statesman House Building,
                  <br />
                  Barakhamba Road, Connaught Place,
                  <br />
                  New Delhi – 110001
                </p>
              </div>
            </div>
          </address>
          <div className="grid sm:grid-cols-2 gap-4">

            <ContactTile
              channel="phone"
              icon={<Phone className="h-4 w-4" />}
              label="Phone"
              value={SITE.phone}
              href={`tel:${SITE.phoneE164}`}
            />
            <ContactTile
              channel="whatsapp"
              icon={<WhatsAppIcon className="h-4 w-4" />}
              label="WhatsApp"
              value="Message on WhatsApp"
              href={WHATSAPP_HREF}
            />
            <ContactTile
              channel="email"
              icon={<Mail className="h-4 w-4" />}
              label="Email"
              value={SITE.email}
              href={`mailto:${SITE.email}`}
            />
            <ContactTile
              channel="linkedin"
              icon={<Linkedin className="h-4 w-4" />}
              label="LinkedIn"
              value="@arifkhan-altimaglobal"
              href={SOCIAL.linkedin}
            />
          </div>
          <div className="mt-6">
            <a
              href={CALENDAR_LINK}
              onClick={ctaClick("calendar", "contact_direct")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline underline-offset-4"
            >
              <Calendar className="h-4 w-4" /> Or book a time on Arif's calendar
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="pb-28">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">Before you write</span>
          <h2 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
            Frequently asked questions
          </h2>
          <dl className="mt-8 divide-y divide-border border-y border-border">
            {CONTACT_FAQS.map((f, i) => (
              <div key={f.q} className="py-6" id={`faq-${i + 1}`}>
                <dt className="text-base md:text-lg font-medium">{f.q}</dt>
                <dd className="mt-2 text-sm md:text-base text-ink-soft leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CONNECT WITH ARIF KHAN */}
      <section className="pb-28">
        <div className="container-page">
          <div className="rounded-2xl border border-border/70 bg-surface/50 backdrop-blur-md p-8 md:p-10">
            <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <span className="eyebrow">Stay Connected</span>
                <h2 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
                  Connect with Arif Khan
                </h2>
                <p className="mt-3 text-sm md:text-base text-ink-soft leading-relaxed">
                  Follow Arif's work on security infrastructure, smart automation, AV and intelligent buildings.
                </p>
              </div>
              <SocialIcons size="lg" />
            </div>
          </div>
        </div>
      </section>

      <LinkedInCarousel
        heading="More from Arif on LinkedIn"
        description="Recent thinking on projects, technology decisions and what makes intelligent environments work."
      />
    </PageShell>
  );
}

function StepBlock({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-balance">{title}</h2>
      {hint && <p className="mt-2 text-sm text-ink-soft">{hint}</p>}
      <div className="mt-8">{children}</div>
    </div>
  );
}

function ChoiceChip({
  label,
  selected,
  onClick,
  multi,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  multi?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        "group relative text-left rounded-2xl border px-5 py-4 text-sm font-medium transition-all",
        "backdrop-blur-md",
        selected
          ? "border-primary bg-primary/10 text-foreground shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.4)]"
          : "border-border bg-surface/40 hover:border-primary/60 hover:bg-surface/70 text-foreground/90",
      ].join(" ")}
    >
      <span className="flex items-center justify-between gap-3">
        <span>{label}</span>
        <span
          className={[
            "inline-flex h-5 w-5 items-center justify-center rounded-full border transition-all",
            multi ? "rounded-md" : "rounded-full",
            selected ? "border-primary bg-primary text-primary-foreground" : "border-border",
          ].join(" ")}
        >
          {selected && <Check className="h-3 w-3" />}
        </span>
      </span>
    </button>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-widest text-ink-soft mb-2 block">
        {label}
        {required && " *"}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-2xl border border-border bg-background/60 px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
      />
    </div>
  );
}

function ContactTile({
  icon,
  label,
  value,
  href,
  channel,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href: string;
  channel: CtaChannel;
}) {
  return (
    <a
      href={href}
      onClick={ctaClick(channel, "contact_direct_tile")}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noreferrer" : undefined}
      className="group flex items-start gap-4 rounded-2xl border border-border/70 bg-surface/50 backdrop-blur-md p-5 hover:border-primary hover:bg-surface transition-all"
    >
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-semibold uppercase tracking-widest text-ink-soft">
          {label}
        </span>
        <span className="mt-1 block text-sm font-medium truncate">{value}</span>
      </span>
    </a>
  );
}
